import { useContext, useEffect } from 'react';
import { FlexContainer, Banner, Text, BannerModesEnum } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import KamiContext from 'Context/KamiContext';
import ReadonlyContext from 'Context/validations';
import useScreen, { SummaryScreenType } from 'Hooks/useScreen';
import useTranslation from 'Hooks/useTranslation';
import ContentContainer from 'Components/UI/ContentContainer';
import FormContainer from 'Components/UI/FormContainer';
import HeaderInfo from 'Components/HeaderInfo';
import { ScreenPropsType } from 'Types/common';
import ValidationSubmit from '../ValidationSubmit';
import HelpInfo from '../HelpInfo';
import AddButton from '../AddButton';
import SummaryCardGroup from './components/SummaryCardGroup';
import * as Styled from './styles';
import { SUMMARY_VALUES } from './utils';

const SummaryScreen = ({ screenName }: ScreenPropsType): JSX.Element => {
  const { t } = useTranslation(locizeNS.VALIDATIONS);
  const { forms, saveData } = useContext(KamiContext);
  const { readOnly, onReadOnly } = useContext(ReadonlyContext);
  const { screen, renderForm, onNextScreen, onPrevScreen, onReloadForm } = useScreen(
    screenName
  ) as SummaryScreenType;

  const summaryCardGroups = screen.getCardGroups();
  const { header, subheader, bannerData, help, helpButtonUrl } = renderForm;

  useEffect(() => {
    saveData({ stage: screen.getSection() });
  }, []);

  const onUpdate = (): void => {
    onReloadForm();
  };

  const onDelete = (): void => {
    saveData({ stage: screen.getSection() });
  };

  const addCardSummary = (): void => {
    const addScreen = screen.add();
    history.push(forms[addScreen.id].path, { childIndex: addScreen.index });
  };

  const emptyImage = SUMMARY_VALUES[screenName]?.image;

  return (
    <ContentContainer testID="summary-section">
      <Styled.Container direction="column" fullWidth fullHeight gap="dimension_16">
        <FormContainer>
          {!screen.hasAtLeastOneCard() ? (
            <FlexContainer direction="column" align="center" gap="dimension_16">
              <Styled.ImageContainer>
                <Styled.Image src={emptyImage} alt="start icon" />
              </Styled.ImageContainer>
              <Text type="paragraphs" size="L_L_S" color="dark" align="center">
                {t(`SummaryEmpty.${screenName}`)}
              </Text>
              <AddButton
                title="AddButton.title"
                onClick={addCardSummary}
                readOnly={readOnly}
                onReadOnly={onReadOnly}
              />
            </FlexContainer>
          ) : (
            <>
              {header && (
                <HeaderInfo info={helpButtonUrl} tag="h2" mb="dimension_32">
                  {header}
                </HeaderInfo>
              )}
              {subheader && (
                <Text type="paragraphs" color="grey" size="S_S_M" mb="dimension_32">
                  {subheader}
                </Text>
              )}
            </>
          )}
          {bannerData && (
            <Banner
              id="summary-banner"
              mode={bannerData?.type as BannerModesEnum}
              label={bannerData?.body}
              {...bannerData}
              mb="dimension_32"
            />
          )}
          {summaryCardGroups?.map(
            summaryCardGroup =>
              summaryCardGroup.list().length > 0 && (
                <SummaryCardGroup
                  key={summaryCardGroup.getId()}
                  summaryCardGroup={summaryCardGroup}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                />
              )
          )}
          {summaryCardGroups.some(item => !item.isReadOnly()) && screen.hasAtLeastOneCard() && (
            <FlexContainer>
              <AddButton
                title="AddButton.title"
                onClick={addCardSummary}
                readOnly={readOnly}
                onReadOnly={onReadOnly}
              />
            </FlexContainer>
          )}
        </FormContainer>
        {help && <HelpInfo info={help} />}
        <ValidationSubmit
          disabled={!screen.isDataValid()}
          onNextScreen={onNextScreen}
          onPrevScreen={onPrevScreen}
          testID="btnSubmit"
        />
      </Styled.Container>
    </ContentContainer>
  );
};

export default SummaryScreen;
